import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const ThemeContext = createContext(null);

const LIGHT = {
  bg: '#FFFFFF', bgAlt: '#F9FAFB', bgCard: '#FFFFFF',
  text: '#1F2937', textMuted: '#6B7280', textLight: '#9CA3AF',
  border: '#E5E7EB', shadow: 'rgba(0,0,0,0.08)',
  inputBg: '#FFFFFF', navbarBg: '#FFFFFF',
  footerBg: '#1F2937', footerText: '#D1D5DB',
  cardShadow: '0 4px 20px rgba(0,0,0,0.05)',
  cardHoverShadow: '0 16px 40px rgba(0,0,0,0.08)',
  gradient: 'linear-gradient(135deg, #FFF7ED 0%, #FEF2F2 30%, #EFF6FF 70%, #F0FDF4 100%)',
  sectionBg: '#F9FAFB',
};

const DARK = {
  bg: '#111827', bgAlt: '#1F2937', bgCard: '#1F2937',
  text: '#F3F4F6', textMuted: '#9CA3AF', textLight: '#6B7280',
  border: '#374151', shadow: 'rgba(0,0,0,0.3)',
  inputBg: '#374151', navbarBg: '#111827',
  footerBg: '#0B0F19', footerText: '#9CA3AF',
  cardShadow: '0 4px 20px rgba(0,0,0,0.2)',
  cardHoverShadow: '0 16px 40px rgba(0,0,0,0.3)',
  gradient: 'linear-gradient(135deg, #111827 0%, #1F2937 50%, #111827 100%)',
  sectionBg: '#1F2937',
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    try {
      const saved = localStorage.getItem('farmdirectly_theme');
      if (saved) return saved === 'dark';
      return window.matchMedia?.('(prefers-color-scheme: dark)').matches || false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    localStorage.setItem('farmdirectly_theme', isDark ? 'dark' : 'light');

    // Inject/remove dark mode overrides for inline styles
    let style = document.getElementById('fd-dark-overrides');
    if (isDark) {
      if (!style) {
        style = document.createElement('style');
        style.id = 'fd-dark-overrides';
        document.head.appendChild(style);
      }
      style.textContent = `
        body { background-color: #111827 !important; color: #F3F4F6 !important; }
        #root > div, #root > div > div { color: #F3F4F6; }
        input, textarea, select { background-color: #374151 !important; color: #F3F4F6 !important; border-color: #4B5563 !important; }
        input::placeholder, textarea::placeholder { color: #9CA3AF !important; }
        .btn-outline-secondary { color: #D1D5DB !important; border-color: #4B5563 !important; }
        .btn-outline-secondary:hover { background-color: #374151 !important; }
        h1, h2, h3, h4, h5, h6 { color: #F3F4F6 !important; }
        p { color: #D1D5DB; }
        a:not(.btn):not(.nav-link) { color: #93C5FD; }
        hr { border-color: #374151 !important; }
        .text-muted { color: #9CA3AF !important; }
        option { background-color: #1F2937; color: #F3F4F6; }
      `;
    } else {
      if (style) style.remove();
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark((prev) => !prev);
  const colors = useMemo(() => isDark ? DARK : LIGHT, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, colors }}>
      {children}
    </ThemeContext.Provider>
  );
};
